import React, { useState } from 'react';
import { format, addWeeks, subWeeks, startOfWeek, addDays, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const C = { border: '#e2e8f0', bg: '#f8fafc', white: '#fff', text: '#1e293b', muted: '#64748b', primary: '#6366f1' };

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// Gera horários de 1 em 1 hora, de 08:00 até 18:00
const HOURS = Array.from({ length: 11 }, (_, i) => {
  const h = i + 8;
  return `${h.toString().padStart(2, '0')}:00`;
});

export default function WeeklyCalendarPicker({ disponibilidade = [], agendamentos = [], selectedDate, selectedTime, onSelect }) {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));

  const nextWeek = () => setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  const prevWeek = () => setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  const currentWeek = () => setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Helper para verificar se um dia/hora específico está dentro da disponibilidade
  const isAvailable = (diaSemana, timeStr) => {
    if (!disponibilidade || disponibilidade.length === 0) return false;

    const dispDia = disponibilidade.find(d => d.dia === diaSemana);
    if (!dispDia) return false;

    const slotTime = parseInt(timeStr.split(':')[0], 10);
    const inicio = parseInt(dispDia.horaInicio.split(':')[0], 10);
    const fim = parseInt(dispDia.horaFim.split(':')[0], 10);

    return slotTime >= inicio && slotTime < fim;
  };

  const isOccupied = (date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return agendamentos.some(a => a.data === dateStr && a.hora === hour);
  };

  const isSelected = (date, time) => {
    return selectedDate === format(date, 'yyyy-MM-dd') && selectedTime === time;
  };

  return (
    <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Header - Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${C.border}`, background: C.bg }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={prevWeek} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, color: C.text }}>&lt;</button>
          <button onClick={nextWeek} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 700, color: C.text }}>&gt;</button>
          <button onClick={currentWeek} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '0.8rem', color: C.text }}>Hoje</button>
        </div>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: C.text }}>
          {format(currentWeekStart, "MMMM 'de' yyyy", { locale: ptBR })}
        </div>
      </div>

      {/* Calendar Grid */}
      <div style={{ display: 'flex', flex: 1, overflowX: 'auto', padding: '16px' }}>
        {/* Days Columns */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, (disponibilidade.length || 7))}, 1fr)`, flex: 1, minWidth: '100%', gap: '12px' }}>
          {days
            .filter(date => !disponibilidade.length || disponibilidade.some(d => d.dia === date.getDay()))
            .map((date, index) => {
            const isToday = isSameDay(date, new Date());
            const diaSemana = date.getDay();
            
            return (
              <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Day Header */}
                <div style={{ height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: isToday ? `${C.primary}10` : '#f1f5f9', borderRadius: 12, marginBottom: '12px', border: isToday ? `1px solid ${C.primary}50` : '1px solid transparent' }}>
                  <span style={{ fontSize: '0.65rem', fontWeight: 800, color: isToday ? C.primary : C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{DIAS_SEMANA[diaSemana]}</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: isToday ? C.primary : C.text, lineHeight: 1, marginTop: '2px' }}>{format(date, 'dd')}</span>
                </div>

                {/* Time Slots */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {HOURS.map(hour => {
                    const available = isAvailable(diaSemana, hour);
                    const occupied = isOccupied(date, hour);
                    const selected = isSelected(date, hour);
                    const canSelect = available && !occupied;

                    if (!available && !occupied && !selected) return null; // Hide completely empty slots
                  
                  return (
                    <div 
                      key={hour} 
                      onClick={() => canSelect && onSelect(format(date, 'yyyy-MM-dd'), hour)}
                      style={{ 
                        height: 40, 
                        borderRadius: 10,
                        background: selected ? C.primary : occupied ? '#fee2e2' : available ? '#e0e7ff' : '#f8fafc',
                        border: canSelect && !selected ? `1px solid #c7d2fe` : occupied ? `1px solid #fca5a5` : `1px solid transparent`,
                        cursor: canSelect ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.15s',
                        opacity: (available || occupied) ? 1 : 0.4
                      }}
                      onMouseEnter={e => { if (canSelect && !selected) { e.currentTarget.style.background = '#c7d2fe'; e.currentTarget.style.transform = 'scale(1.02)'; } }}
                      onMouseLeave={e => { if (canSelect && !selected) { e.currentTarget.style.background = '#e0e7ff'; e.currentTarget.style.transform = 'scale(1)'; } }}
                    >
                      <span style={{ 
                        fontSize: '0.8rem', 
                        fontWeight: 800, 
                        color: selected ? '#fff' : occupied ? '#ef4444' : canSelect ? C.primary : C.muted 
                      }}>
                        {selected ? `✓ ${hour}` : occupied ? `Ocupado` : hour}
                      </span>
                    </div>
                  );
                })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
